import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Copy,
  Check,
  Share2,
  Gift,
  ShieldCheck,
  Sparkles,
  Award,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';
import type { ReferralItemDto } from '@nova-mine/shared';
import { formatCoinAmount } from '@nova-mine/shared';
import { GlassCard } from '../components/common/GlassCard.js';
import { GradientCard } from '../components/common/GradientCard.js';
import { AnimatedButton } from '../components/common/AnimatedButton.js';
import { sound } from '../utils/sound.js';
import { api } from '../services/api.js';

interface ReferralScreenProps {
  referralCode: string;
  referralCount: number;
  totalReward: number;
  openTelegramLink: (url: string) => void;
}

export const ReferralScreen: React.FC<ReferralScreenProps> = ({
  referralCode,
  referralCount,
  totalReward,
  openTelegramLink,
}) => {
  const [copied, setCopied] = useState(false);
  const [referrals, setReferrals] = useState<ReferralItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'NovaMineBot';
  const miniAppName = import.meta.env.VITE_TELEGRAM_MINI_APP_NAME || '';

  const referralLink = miniAppName
    ? `https://t.me/${botUsername}/${miniAppName}?startapp=${referralCode}`
    : `https://t.me/${botUsername}?startapp=${referralCode}`;

  const fetchReferrals = async () => {
    try {
      setIsLoading(true);
      const res = await api.getReferrals();
      setReferrals(res.referrals || []);
    } catch {
      setReferrals([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  const handleCopy = () => {
    sound.playTap();
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShare = async () => {
    sound.playTap();
    const text = `Join NOVA MINE! Mine 2,000 coins daily + unlock Auto Miner and Telegram Stars miners.`;
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(text)}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join NOVA MINE',
          text,
          url: referralLink,
        });
        return;
      } catch {
        // Fallback to Telegram link
      }
    }

    openTelegramLink(shareUrl);
  };

  return (
    <div className="w-full space-y-5 animate-fade-in pb-4">
      {/* 1. Referral Bonus Radiant Hero */}
      <GlassCard variant="magenta" className="border-pink-500/35 p-6 text-center relative overflow-hidden">
        {/* Animated Background Network Connection Motif */}
        <svg
          className="absolute inset-0 w-full h-full opacity-15 pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line x1="20%" y1="30%" x2="50%" y2="50%" stroke="#ec4899" strokeWidth="1.5" strokeDasharray="3 3" />
          <line x1="80%" y1="30%" x2="50%" y2="50%" stroke="#00f2fe" strokeWidth="1.5" strokeDasharray="3 3" />
          <line x1="50%" y1="50%" x2="30%" y2="80%" stroke="#ffd700" strokeWidth="1.5" strokeDasharray="3 3" />
          <line x1="50%" y1="50%" x2="70%" y2="80%" stroke="#a855f7" strokeWidth="1.5" strokeDasharray="3 3" />
          <circle cx="50%" cy="50%" r="5" fill="#ec4899" />
          <circle cx="20%" cy="30%" r="3" fill="#00f2fe" />
          <circle cx="80%" cy="30%" r="3" fill="#ffd700" />
          <circle cx="30%" cy="80%" r="3" fill="#a855f7" />
          <circle cx="70%" cy="80%" r="3" fill="#ec4899" />
        </svg>

        <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-tr from-pink-500 to-purple-600 flex items-center justify-center text-white mb-3 shadow-xl shadow-pink-500/25 border border-pink-400/40">
          <Gift className="w-7 h-7 text-white" />
        </div>

        <h3 className="text-xl font-black text-white flex items-center justify-center space-x-1.5">
          <span>Earn 10,000 NOVA Per Referral</span>
          <Sparkles className="w-4 h-4 text-pink-300" />
        </h3>

        <p className="text-xs text-pink-200/90 mt-1 max-w-xs mx-auto leading-relaxed">
          Invite friends to start mining. When they activate their account, you receive a guaranteed <strong className="text-nova-gold font-mono">+10,000 NOVA</strong> ledger credit.
        </p>

        {/* Stats Grid */}
        <div className="mt-5 grid grid-cols-2 gap-3 text-left">
          <div className="rounded-2xl bg-black/35 border border-white/10 p-3.5 backdrop-blur-md">
            <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider block">
              Friends Recruited
            </span>
            <span className="font-mono font-black text-2xl text-white mt-1 block">
              {referralCount}
            </span>
          </div>

          <div className="rounded-2xl bg-black/35 border border-white/10 p-3.5 backdrop-blur-md">
            <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider block">
              Total Referral Bounty
            </span>
            <span className="font-mono font-black text-2xl text-gold-gradient mt-1 block">
              {formatCoinAmount(totalReward, 0)}
            </span>
          </div>
        </div>
      </GlassCard>

      {/* 2. Referral Link & Copy / Share Actions */}
      <GlassCard variant="purple" className="border-purple-500/30 p-5 space-y-3">
        <label className="text-xs font-black text-purple-200 uppercase tracking-wider block">
          Your Personal Invitation Link
        </label>

        <div className="flex items-center space-x-2">
          <div className="flex-1 bg-black/45 border border-white/10 rounded-2xl px-4 py-3 text-xs font-mono text-gray-200 truncate select-all">
            {referralLink}
          </div>

          <AnimatedButton
            variant="cyan"
            size="md"
            onClick={handleCopy}
            icon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          >
            {copied ? 'Copied!' : 'Copy'}
          </AnimatedButton>
        </div>

        <AnimatedButton
          variant="primary"
          size="lg"
          onClick={handleShare}
          icon={<Share2 className="w-5 h-5" />}
          className="w-full justify-center mt-2 shadow-purple-500/25"
        >
          Share Invitation to Telegram
        </AnimatedButton>
      </GlassCard>

      {/* 3. Referral History List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h4 className="text-xs font-black uppercase tracking-wider text-gray-300 flex items-center space-x-2">
            <Users className="w-3.5 h-3.5 text-pink-400" />
            <span>Recruited Pioneers ({referrals.length})</span>
          </h4>
          <span className="text-[11px] text-pink-400 font-mono font-bold">
            +10,000 / friend
          </span>
        </div>

        {referrals.length === 0 ? (
          <GlassCard variant="deep" className="text-center py-8">
            <Users className="w-10 h-10 text-gray-600 mx-auto mb-2" />
            <h5 className="text-sm font-bold text-gray-300">No Referrals Yet</h5>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
              Share your personal link above to recruit fellow pioneers and unlock +10,000 NOVA bonuses.
            </p>
          </GlassCard>
        ) : (
          <div className="space-y-2.5">
            {referrals.map((r) => (
              <GlassCard key={r.id} variant="deep" className="p-3.5 border-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 rounded-xl bg-pink-500/20 border border-pink-500/30 flex items-center justify-center font-bold text-pink-300 text-xs">
                      {r.name ? r.name.charAt(0).toUpperCase() : 'P'}
                    </div>
                    <div>
                      <h5 className="font-bold text-white text-xs">
                        {r.name || 'Nova Pioneer'}
                      </h5>
                      <span className="text-[10px] text-gray-400 font-mono">
                        Pioneer #{r.referee_id || r.id}
                      </span>
                    </div>
                  </div>

                  <div className="text-right">
                    <span className="text-xs font-black font-mono text-nova-gold">
                      +{formatCoinAmount(r.reward || 10000, 0)}
                    </span>
                    <span className="text-[10px] text-gray-500 block">
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
